import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
  Tooltip,
} from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ReceiptIcon from "@material-ui/icons/Receipt";
import RoomIcon from "@material-ui/icons/Room";
import { format, parseISO } from "date-fns";
import PropTypes from "prop-types";
import Row from "./Row";
import { formatCents } from "./utils";

const orderDate = (order) => {
  try {
    return format(parseISO(order.created), "PPP p");
  } catch {
    return order.created;
  }
};

export default function Summary({ order }) {
  const requiresShipping = (order?.shipments ?? []).filter(
    (shipment) => !shipment.hasNoShipping,
  );

  return (
    <Grid item xs={12}>
      <Card elevation={0}>
        <CardHeader
          title={
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
          }
          subheader={
            <Typography color="textSecondary" variant="body2">
              Placed on {orderDate(order)} • Receipt # {order.receiptNumber}
            </Typography>
          }
        />
        {order.redacted ? null : (
          <CardContent>
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                md={requiresShipping.length > 0 ? 5 : 8}
                xl={requiresShipping.length > 0 ? 4 : 7}
              >
                <Box display="grid" gridGap={8}>
                  <Box display="flex" alignItems="center">
                    <MailOutlineIcon style={{ fontSize: 18, marginRight: 6 }} />

                    <Typography style={{ wordBreak: "break-word" }}>
                      {order.receiptEmail}
                    </Typography>
                  </Box>

                  {requiresShipping.length > 0 ? (
                    <Box display="flex" alignItems="center">
                      <ReceiptIcon style={{ fontSize: 18, marginRight: 6 }} />

                      <Typography>
                        {requiresShipping.length} shipment
                        {requiresShipping.length === 1 ? "" : "s"}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Grid>

              {/* Shipping Address */}
              {requiresShipping.length > 0 && (
                <Grid item xs={12} md={3} xl={4}>
                  <Typography variant="subtitle2">Shipping Address</Typography>

                  <Box mt={1} display="flex" alignItems="flex-start">
                    <RoomIcon style={{ fontSize: 18, marginRight: 6 }} />

                    <Typography variant="body2">
                      {order.address?.firstName} {order.address?.lastName}
                      <br />
                      {order.address?.line1}
                      {order.address?.line2 ? `, ${order.address.line2}` : ""}
                      <br />
                      {order.address?.city}, {order.address?.state}{" "}
                      {order.address?.postalCode}
                      <br />
                      {order.address?.country}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Totals */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Order Totals</Typography>
                <Box mt={1} display="grid">
                  <Row
                    label="Subtotal"
                    value={formatCents(order.subtotalWithBump, order.currency)}
                  />

                  {order.serviceFee > 0 && (
                    <Row
                      label="Service Fee"
                      value={formatCents(order.serviceFee, order.currency)}
                    />
                  )}

                  {order.promoCodeDiscount !== 0 &&
                    order.promoCodeAppliesTo === "subtotal" && (
                      <Row
                        label="Promo Code"
                        value={formatCents(
                          order.promoCodeDiscount,
                          order.currency,
                        )}
                      />
                    )}

                  {order.tax > 0 && (
                    <Row
                      label="Tax"
                      value={formatCents(order.tax, order.currency)}
                    />
                  )}

                  <Row
                    label="Shipping"
                    value={
                      order.shippingCost > 0
                        ? formatCents(order.shippingCost, order.currency)
                        : "Free"
                    }
                  />

                  {order.promoCodeDiscount !== 0 &&
                    order.promoCodeAppliesTo !== "subtotal" && (
                      <Row
                        label="Promo Code"
                        value={formatCents(
                          order.promoCodeDiscount,
                          order.currency,
                        )}
                      />
                    )}

                  <Box my={1}>
                    <Divider />
                  </Box>

                  <Row
                    label="Total"
                    value={formatCents(order.total, order.currency)}
                    bold
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        )}

        <CardActions>
          <Tooltip title="Questions about the fulfillment timeline? Take a look at our FAQs">
            <Button
              variant="outlined"
              component="a"
              href="https://help.streamily.com/category/5-order-process"
              target="_blank"
              rel="noopener noreferrer"
            >
              Order FAQs
            </Button>
          </Tooltip>

          <Button
            variant="outlined"
            component="a"
            href={`mailto:support@streamily.com?subject=Support%20for%20Order%20${order.receiptNumber}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Support
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
}

Summary.propTypes = {
  order: PropTypes.shape({
    redacted: PropTypes.bool,
    created: PropTypes.string.isRequired,
    receiptNumber: PropTypes.string.isRequired,
    receiptEmail: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    subtotalWithBump: PropTypes.number.isRequired,
    promoCodeDiscount: PropTypes.number,
    promoCodeAppliesTo: PropTypes.string,
    serviceFee: PropTypes.number,
    shippingCost: PropTypes.number,
    shippingRateAdjustment: PropTypes.number,
    tax: PropTypes.number,
    total: PropTypes.number.isRequired,
    address: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      postalCode: PropTypes.string,
      country: PropTypes.string,
    }),
    shipments: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};
